/** One row of a key/value body editor in the Authentication panel. */
type BodyRow = { key?: string; value?: string };

export type ToolClientRuleValues = {
  authType?: string;
  autoSubType?: string;
  authorizeUrlEndpoint?: string;
  tokenRequestBody?: BodyRow[];
  refreshExtraBody?: BodyRow[];
};

/** Values of the rows that are saved (a row without a key or value is dropped on save). */
const savedValues = (rows?: BodyRow[]): string[] =>
  (rows || []).filter((row) => row?.key && row?.value).map((row) => row.value as string);

/**
 * Whether the tool takes one app client, set by whoever configures it, that everyone signs
 * in through. Mirrors the server rule:
 * - OIDC: no brokered authorize URL, or the exchange / refresh body names `{client_id}` or
 *   `{client_secret}`;
 * - password (the default sub-type): a refresh body value is exactly `{{client_id}}` or
 *   `{{client_secret}}`.
 */
export const takesToolClient = (values: ToolClientRuleValues): boolean => {
  if (values.authType !== 'auto') return false;
  const subType = values.autoSubType || 'password';

  if (subType === 'oidc') {
    if (!values.authorizeUrlEndpoint) return true;
    return [...savedValues(values.tokenRequestBody), ...savedValues(values.refreshExtraBody)].some(
      (value) => value.includes('{client_id}') || value.includes('{client_secret}')
    );
  }

  if (subType === 'password') {
    return savedValues(values.refreshExtraBody).some(
      (value) => value === '{{client_id}}' || value === '{{client_secret}}'
    );
  }

  return false;
};
